import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogPortal,
  DialogOverlay,
} from "../ui/dialog";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X, ArrowUpRight, Truck, CreditCard, Award, Beaker, Clock3, Sparkles, ShoppingBag, MessageCircle } from "lucide-react";
import { useCart } from "../../context/CartContext";
import { useChat } from "../../context/ChatContext";
import { useTheme } from "../../context/ThemeContext";
import { isPlaceholderPrice, priceLabel } from "../../utils/price";
import { stockForVariant, priceForSize } from "../../utils/variant";
import { recommendedSize, compareAtForSize, formatGroups, recommendedLabel } from "../../utils/formatMetrics";
import { codProvinces, codMinAmount, formatProvinceList } from "../../utils/shipping";
import { getTierNames } from "../../utils/tierNames";

const tierAccentColor = {
  start: "text-[#94A3B8]",
  power: "text-[#C0C0C0]",
  elite: "text-[#E5E4E2]",
};

const ProductDetailModal = ({ product, open, onOpenChange }) => {
  const cart = useCart();
  const chat = useChat();
  const { settings } = useTheme();
  const flavors = Array.isArray(product?.flavors) ? product.flavors : [];
  const sizes = Array.isArray(product?.sizes) ? product.sizes : [];
  const galleryImages = (Array.isArray(product?.images) && product.images.length > 0
    ? product.images
    : (product?.image ? [product.image] : [])
  ).filter(Boolean);
  const [pickedFlavor, setPickedFlavor] = useState("");
  const [pickedSize, setPickedSize] = useState("");
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    if (!open) return;
    setPickedFlavor(flavors[0] || "");
    setPickedSize(sizes[0] || "");
    setActiveImg(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, product?.id]);

  if (!product) return null;
  const tierNames = getTierNames(settings);
  const accent = { label: `Linea ${tierNames[product.tier]?.name || product.tier}`, color: tierAccentColor[product.tier] };
  const mainImage = galleryImages[activeImg] || product.image || "";
  const isExternalPhoto = mainImage && (mainImage.startsWith("http") || mainImage.startsWith("/api/uploads"));
  const variantStock = product?.variant_stock || {};
  const selectedComboStock = stockForVariant(variantStock, pickedFlavor, pickedSize);
  const isOutOfStock = selectedComboStock !== undefined ? selectedComboStock <= 0 : Number(product.stock || 0) <= 0;
  const displayPrice = priceForSize(product, pickedSize);
  const compareAt = compareAtForSize(product, pickedSize);
  const recSize = settings?.layout?.show_recommended !== false ? recommendedSize(product) : "";
  const priceGroups = settings?.layout?.show_price_comparison !== false && !settings?.layout?.hide_prices ? formatGroups(product) : [];

  const addAndClose = () => {
    cart.addItem(product, 1, { flavor: pickedFlavor, size: pickedSize });
    onOpenChange(false);
  };
  const askRenato = () => {
    onOpenChange(false);
    const variant = [pickedFlavor, pickedSize].filter(Boolean).join(" ");
    chat.openWith(`Mi interessa *${product.name}* (${product.brand})${variant ? ` — ${variant}` : ""}. Puoi darmi maggiori informazioni e il prezzo?`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay className="fixed inset-0 z-50 bg-[#060A14]/85 backdrop-blur-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          data-testid="product-detail-modal"
          className="fixed left-1/2 top-1/2 z-50 w-[95vw] max-w-5xl max-h-[92vh] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-[10px] border border-white/10 bg-[#0A1121] shadow-2xl data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
        >
          <DialogPrimitive.Title className="sr-only">{product.name}</DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">{product.desc}</DialogPrimitive.Description>

          <button
            onClick={() => onOpenChange(false)}
            data-testid="modal-close-btn"
            className="absolute right-4 top-4 z-20 inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#060A14]/80 border border-white/20 text-white/80 hover:bg-white hover:text-[#060A14] transition-all duration-300"
            aria-label="Chiudi"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-2 max-h-[92vh] overflow-y-auto">
            {/* IMAGE COLUMN */}
            <div
              className={`relative aspect-square lg:aspect-auto lg:min-h-[600px] ${
                isExternalPhoto
                  ? "bg-gradient-to-b from-[#F4F5F7] to-[#D9DDE3]"
                  : "bg-[#060A14]"
              }`}
            >
              <img
                src={mainImage}
                alt={product.name}
                data-testid="modal-product-image"
                className={`absolute inset-0 w-full h-full ${
                  isExternalPhoto ? "object-contain p-10" : "object-cover"
                }`}
              />
              {/* tier badge top-left */}
              <div className="absolute top-4 left-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#060A14]/80 backdrop-blur-md border border-[#C0C0C0]/40">
                {product.tier === "elite" && <Award className="w-3 h-3 text-[#C0C0C0]" />}
                <span className="text-[10px] tracking-[0.32em] uppercase text-[#E5E4E2]">
                  {accent.label}
                </span>
              </div>

              {/* Thumbnail gallery — only when multiple images */}
              {galleryImages.length > 1 && (
                <div className="absolute bottom-3 left-3 right-3 flex gap-2 overflow-x-auto pb-1"
                     data-testid="modal-thumbs">
                  {galleryImages.map((url, i) => (
                    <button
                      key={`${url}-${i}`}
                      type="button"
                      onClick={() => setActiveImg(i)}
                      data-testid={`modal-thumb-${i}`}
                      aria-label={`Foto ${i + 1}`}
                      className={`shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-sm overflow-hidden border-2 bg-white p-0.5 transition-all ${
                        i === activeImg ? "border-[#C0C0C0] scale-105 shadow-lg" : "border-white/30 opacity-80 hover:opacity-100"
                      }`}
                    >
                      <img src={url} alt="" className="w-full h-full object-contain" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* CONTENT COLUMN */}
            <div className="relative flex flex-col p-6 sm:p-8 lg:p-10 gap-6 bg-[#0A1121]">
              <div>
                <span className={`text-[11px] tracking-[0.32em] uppercase ${accent.color}`}>
                  {product.brand}
                </span>
                <h2
                  className="mt-2 font-[Outfit] text-white tracking-tight font-light leading-tight"
                  style={{ fontSize: "clamp(1.6rem, 3vw, 2.4rem)" }}
                  data-testid="modal-product-name"
                >
                  {product.name}
                </h2>
                <div className="mt-4 flex items-baseline gap-4">
                  {settings?.layout?.hide_prices ? (
                    <span
                      className="font-[Outfit] text-[#EF4444] text-xl tracking-tight inline-flex items-center gap-2 px-3 py-1.5 rounded-sm border border-[#EF4444]/40 bg-[#EF4444]/10 uppercase"
                      data-testid="modal-price-terminated"
                    >
                      Prodotto terminato
                    </span>
                  ) : isPlaceholderPrice(product.price) || product.custom_price_label ? (
                    <span
                      className="font-[Outfit] text-[#E5E4E2] text-xl tracking-tight inline-flex items-center gap-2 px-3 py-1.5 rounded-sm border border-[#C0C0C0]/30 bg-[#060A14]/40"
                      data-testid="modal-price-on-request"
                    >
                      {priceLabel(product, settings)}
                    </span>
                  ) : (
                    <span
                      className="font-[Outfit] text-white text-2xl tracking-tight inline-flex items-center gap-3 px-3 py-1.5 rounded-sm border border-[#C0C0C0]/40 bg-[#060A14]/40"
                      data-testid="modal-price"
                    >
                      {compareAt > displayPrice && (
                        <span className="text-[#94A3B8] text-base line-through" data-testid="modal-compare-at">€{Number(compareAt).toFixed(2)}</span>
                      )}
                      <span>€{Number(displayPrice).toFixed(2)}</span>
                    </span>
                  )}
                  {product.format && (
                    <span className="text-[12px] text-[#94A3B8] tracking-[0.16em] uppercase">
                      {product.format}
                    </span>
                  )}
                </div>
              </div>

              <p className="text-[14px] text-[#94A3B8] leading-relaxed font-[Manrope] border-l border-[#C0C0C0]/30 pl-4">
                {product.desc}
              </p>

              {(flavors.length > 0 || sizes.length > 0) && (
                <section data-testid="modal-variants" className="space-y-3 pt-1">
                  {flavors.length > 0 && (
                    <div>
                      <h4 className="text-[10px] tracking-[0.32em] uppercase text-[#C0C0C0] mb-2">Scegli il gusto</h4>
                      <div className="flex flex-wrap gap-2">
                        {flavors.map((f) => {
                          const stock = stockForVariant(variantStock, f, pickedSize);
                          const esaurito = stock !== undefined && stock <= 0;
                          return (
                          <button key={f} onClick={() => !esaurito && setPickedFlavor(f)} data-testid={`modal-flavor-${f}`}
                                  disabled={esaurito}
                                  className={`px-3 py-1.5 rounded-full text-[11px] tracking-[0.18em] uppercase border transition-colors ${
                                    pickedFlavor === f ? "bg-[#C0C0C0] text-[#060A14] border-[#C0C0C0]" : "border-white/15 text-[#94A3B8] hover:text-white"
                                  } ${esaurito ? "opacity-40 line-through cursor-not-allowed" : ""}`}>{f}{esaurito ? " (Esaurito)" : ""}</button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {sizes.length > 0 && (
                    <div>
                      <h4 className="text-[10px] tracking-[0.32em] uppercase text-[#C0C0C0] mb-2">Scegli il formato</h4>
                      <div className="flex flex-wrap gap-2">
                        {sizes.map((sz) => {
                          const stock = stockForVariant(variantStock, pickedFlavor, sz);
                          const esaurito = stock !== undefined && stock <= 0;
                          const isRec = sz === recSize;
                          return (
                          <button key={sz} onClick={() => !esaurito && setPickedSize(sz)} data-testid={`modal-size-${sz}`}
                                  disabled={esaurito}
                                  className={`px-3 py-1.5 rounded-full text-[11px] tracking-[0.18em] uppercase border transition-colors ${
                                    pickedSize === sz ? "bg-[#C0C0C0] text-[#060A14] border-[#C0C0C0]" : isRec ? "border-emerald-400/60 text-emerald-200" : "border-white/15 text-[#94A3B8] hover:text-white"
                                  } ${esaurito ? "opacity-40 line-through cursor-not-allowed" : ""}`}>{sz}{esaurito ? " (Esaurito)" : ""}{isRec ? " ★" : ""}</button>
                          );
                        })}
                      </div>
                      {recSize && (
                        <p className="mt-2 text-[10px] text-emerald-300/90 tracking-[0.14em] uppercase" data-testid="modal-recommended-note">
                          ★ {recommendedLabel(product, settings)}: {recSize} — miglior prezzo
                        </p>
                      )}
                    </div>
                  )}
                </section>
              )}

              {priceGroups.length > 0 && (
                <section data-testid="modal-format-comparison" className="pt-1">
                  <h4 className="text-[10px] tracking-[0.32em] uppercase text-[#C0C0C0] mb-2">Confronto formati</h4>
                  <div className="space-y-3">
                    {priceGroups.map((g) => (
                      <table key={g.group} className="w-full text-[12px]">
                        <thead className="text-[9px] tracking-[0.2em] uppercase text-[#94A3B8]">
                          <tr>
                            <th className="text-left font-normal pb-1.5">Formato</th>
                            <th className="text-right font-normal pb-1.5">Prezzo</th>
                            <th className="text-right font-normal pb-1.5">€/{g.per100Label}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {g.rows.map((r) => (
                            <tr key={r.size} className={`border-t border-white/5 ${r.isBest ? "text-emerald-200" : "text-[#E5E4E2]"}`}>
                              <td className="py-1.5 uppercase tracking-wider">{r.size}{r.isBest ? " ★" : ""}</td>
                              <td className="py-1.5 text-right">€{Number(r.price).toFixed(2)}</td>
                              <td className="py-1.5 text-right font-[Outfit]">€{r.per100.toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ))}
                  </div>
                </section>
              )}

              {/* INGREDIENTI */}
              {Array.isArray(product.ingredients) && product.ingredients.length > 0 && (
                <section data-testid="modal-ingredients">
                  <div className="flex items-center gap-2 mb-3">
                    <Beaker className="w-4 h-4 text-[#C0C0C0]" strokeWidth={1.5} />
                    <h3 className="text-[11px] tracking-[0.32em] uppercase text-[#C0C0C0]">
                      Composizione
                    </h3>
                  </div>
                  <ul className="space-y-1.5">
                    {product.ingredients.map((ing, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-3 text-[13px] text-[#E5E4E2] font-[Manrope] leading-relaxed"
                      >
                        <span className="mt-2 h-1 w-1 rounded-full bg-[#C0C0C0] shrink-0" />
                        <span>{ing}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
              {typeof product.ingredients === "string" && product.ingredients.trim() && (
                <section data-testid="modal-ingredients-text">
                  <div className="flex items-center gap-2 mb-3">
                    <Beaker className="w-4 h-4 text-[#C0C0C0]" strokeWidth={1.5} />
                    <h3 className="text-[11px] tracking-[0.32em] uppercase text-[#C0C0C0]">
                      Composizione
                    </h3>
                  </div>
                  <p className="text-[13px] text-[#E5E4E2] font-[Manrope] leading-relaxed whitespace-pre-line">
                    {product.ingredients}
                  </p>
                </section>
              )}

              {/* DOSAGGIO */}
              {product.dosage && (
                <section data-testid="modal-dosage">
                  <div className="flex items-center gap-2 mb-3">
                    <Clock3 className="w-4 h-4 text-[#C0C0C0]" strokeWidth={1.5} />
                    <h3 className="text-[11px] tracking-[0.32em] uppercase text-[#C0C0C0]">
                      Modo d'uso
                    </h3>
                  </div>
                  <p className="text-[13px] text-[#E5E4E2] font-[Manrope] leading-relaxed">
                    {product.dosage}
                  </p>
                </section>
              )}

              {/* BENEFICI */}
              {Array.isArray(product.benefits) && product.benefits.length > 0 && (
                <section data-testid="modal-benefits">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="w-4 h-4 text-[#C0C0C0]" strokeWidth={1.5} />
                    <h3 className="text-[11px] tracking-[0.32em] uppercase text-[#C0C0C0]">
                      Benefici principali
                    </h3>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {product.benefits.map((b, i) => (
                      <span
                        key={i}
                        className="px-3 py-1.5 rounded-full text-[11px] tracking-[0.06em] bg-white/5 border border-[#C0C0C0]/20 text-[#E5E4E2]"
                      >
                        {b}
                      </span>
                    ))}
                  </div>
                </section>
              )}

              {/* shipping / payments row */}
              <div className="grid grid-cols-2 gap-3 pt-2 border-t border-white/10 mt-2">
                <div className="flex items-start gap-2">
                  <Truck className="w-4 h-4 text-[#C0C0C0] mt-0.5" strokeWidth={1.5} />
                  <div className="flex flex-col">
                    <span className="text-[10px] tracking-[0.24em] uppercase text-[#94A3B8]">
                      Spedizione
                    </span>
                    <span className="text-[12px] text-[#E5E4E2]">3-5 giorni</span>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <CreditCard className="w-4 h-4 text-[#C0C0C0] mt-0.5" strokeWidth={1.5} />
                  <div className="flex flex-col">
                    <span className="text-[10px] tracking-[0.24em] uppercase text-[#94A3B8]">
                      Pagamento
                    </span>
                    <span className="text-[12px] text-[#E5E4E2]">Carta · PayPal · COD*</span>
                  </div>
                </div>
              </div>
              <p className="text-[10px] text-[#94A3B8] -mt-1">
                *COD: solo {formatProvinceList(codProvinces(settings)) || "—"}{Number(codMinAmount(settings)) > 0 ? ` con ordine minimo ${Number(codMinAmount(settings)).toFixed(2)}€` : ""}
              </p>

              {/* CTAs */}
              <div className="mt-auto flex flex-col gap-2">
                <button
                  onClick={addAndClose}
                  disabled={isOutOfStock}
                  data-testid="modal-add-to-cart"
                  className="group inline-flex items-center justify-between gap-3 bg-[#C0C0C0] text-[#060A14] px-6 py-4 rounded-sm text-[12px] tracking-[0.28em] uppercase font-medium hover:bg-white transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <span className="flex items-center gap-3">
                    <ShoppingBag className="w-5 h-5" />
                    {isOutOfStock ? "Prodotto esaurito" : "Aggiungi al carrello"}
                  </span>
                  {!isOutOfStock && <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />}
                </button>
                <button
                  onClick={askRenato}
                  data-testid="modal-chat-cta"
                  className="inline-flex items-center justify-center gap-3 bg-transparent border border-emerald-400/40 text-[#E5E4E2] px-6 py-3.5 rounded-sm text-[11px] tracking-[0.28em] uppercase hover:bg-emerald-400/10 transition-all duration-300"
                >
                  <MessageCircle className="w-4 h-4 text-emerald-400" />
                  Chiedi a Leo
                </button>
              </div>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
};

export default ProductDetailModal;
